/* eslint-disable react/jsx-props-no-spreading */
import { Suspense, useEffect, useState } from 'react';

import cx from 'classnames';
import { t } from 'i18next';
import { Freeze } from 'react-freeze';

import { setIsChartFrozen } from 'actions/tradingView';
import Render from 'components/render';
import { MIXPANEL_EVENT_NAMES } from 'constants/mixpanelEventNames';
import { URL_SEARCH_PARAMS_KEYS } from 'constants/urlSearchParams';
import { trackMixpanelEvent } from 'helpers/mixpanel-init';
import { useRouterSearchParams } from 'hooks/useRouterSearchParams';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { chartActiveTabSelector } from 'selectors/tradingViewSelector';
import { useAppDispatch, useAppSelector } from 'storeHooks';
import { SettingIcon } from 'styles/assets/icons';
import { ContractType } from 'types/IProducts';
import Box from 'UIKit/Box';

import { CHART_ORDERBOOK_SECTION_TABS } from './ChartOrderbookTab.constants';
import classes from './ChartOrderbookTab.module.scss';
import { ChartTab, ChartTabs } from './ChartTabs';
import ChartTypeDropdown from './chartTypeDropdown';
import { Chart, OrderBook, RecentTrade } from './components';
import SettingsModal from './SettingsModal';

const tabList = [
  {
    tabId: CHART_ORDERBOOK_SECTION_TABS.ORDERBOOK,
    title: t('trading:tabList.orderBook'),
    'data-testid': 'mobile-orderbook-tab',
  },
  {
    tabId: CHART_ORDERBOOK_SECTION_TABS.RECENT_TRADES,
    title: t('trading:tabList.recentTrade'),
    'data-testid': 'recent-trades-tab',
  },
];

const chartLabel = t('trading:tabList.charts');

const ChartOrderBookTab = () => {
  const searchParams = useRouterSearchParams();
  const dispatch = useAppDispatch();
  const chartActiveTab = useAppSelector(chartActiveTabSelector);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const selectedProduct = useAppSelector(selectedProductSelector);
  const symbol = selectedProduct?.symbol;
  const productType = selectedProduct?.contract_type;

  const [activeTab, setActiveTab] = useState<string>('');

  const updateQueryParam = (value: string) => {
    const urlSearchParams = new URLSearchParams({
      [URL_SEARCH_PARAMS_KEYS.CHART_ORDERBOOK_MOBILE.SECTION_TABS]: value,
    });

    setTimeout(() => {
      // ! reduces long task time of gtag
      window.history.replaceState(
        null,
        '',
        `${window.location.pathname}?${urlSearchParams.toString()}`
      );
    }, 0);
  };

  const handleTabChange = (tabId: string, isChartTab = false) => {
    setActiveTab(tabId);
    dispatch(setIsChartFrozen(tabId !== CHART_ORDERBOOK_SECTION_TABS.CHART));

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.CHART_TYPE.TAB_CLICKED, {
      tab: tabId,
      product_type: productType,
      product_name: symbol,
      ...(isChartTab ? { chart_type: chartActiveTab } : {}),
    });
    updateQueryParam(tabId);
  };

  const handleSettingsIconClick = () => {
    setIsSettingsModalOpen(true);

    trackMixpanelEvent(MIXPANEL_EVENT_NAMES.MOBILE_CHART.SETTINGS_BUTTON_CLICKED, {
      source: activeTab,
    });
  };

  useEffect(() => {
    const activeTabParam = searchParams.get(
      URL_SEARCH_PARAMS_KEYS.CHART_ORDERBOOK_MOBILE.SECTION_TABS
    );
    if (!activeTabParam) {
      updateQueryParam(CHART_ORDERBOOK_SECTION_TABS.CHART);
    }
    setActiveTab(activeTabParam || CHART_ORDERBOOK_SECTION_TABS.CHART);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className={classes.chartOrderbookTabWrapper}>
      <Box flex="flex" className={classes.tabContainer}>
        {/* ideally this tab should stay in place & only dropdown should be disabled, freezing it for now to prevent re render & preserve specific chart tab */}
        <Freeze freeze={activeTab !== CHART_ORDERBOOK_SECTION_TABS.CHART}>
          <Box className={classes.chartTypeDropdownWrapper}>
            <ChartTypeDropdown />
          </Box>
        </Freeze>
        <ChartTabs
          fixedWidthTabs
          className={classes.tabList}
          // ? hide tab indicator when chart tab selected
          value={activeTab === CHART_ORDERBOOK_SECTION_TABS.CHART ? false : activeTab}
          onChange={tabId => handleTabChange(tabId)}
          data-testid="chart-tabs"
        >
          <Render when={activeTab !== CHART_ORDERBOOK_SECTION_TABS.CHART}>
            <ChartTab
              className={cx(classes.tabButton, classes.chartTab)}
              value={CHART_ORDERBOOK_SECTION_TABS.CHART}
              label={chartLabel}
              data-testid="mobile-chart-tab"
              onClick={() => handleTabChange(CHART_ORDERBOOK_SECTION_TABS.CHART, true)}
              index={0}
            />
          </Render>
          {tabList.map((tab, i) => (
            <ChartTab
              className={classes.tabButton}
              label={tab.title}
              key={tab.tabId}
              value={tab.tabId}
              data-testid={tab['data-testid']}
              index={i + 1}
            />
          ))}
        </ChartTabs>
        <Render
          when={
            activeTab === CHART_ORDERBOOK_SECTION_TABS.CHART &&
            selectedProduct?.contract_type !== ContractType.Tracker
          }
        >
          <button
            type="button"
            data-testid="mobile-chart-orderbook-setting-button"
            className={`flex-set flex--content-center flex--align-items-center naked pointer ${classes.settingButtonContainer}`}
            onClick={handleSettingsIconClick}
          >
            <div
              className={cx(
                'flex-set flex--content-center flex--align-items-center naked',
                classes.settingButton
              )}
            >
              <SettingIcon width={16} height={16} />
            </div>
          </button>
        </Render>
      </Box>
      <Suspense fallback={null}>
        <Freeze freeze={activeTab !== CHART_ORDERBOOK_SECTION_TABS.CHART}>
          <Chart />
        </Freeze>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.ORDERBOOK}>
          <OrderBook />
        </Render>
        <Render when={activeTab === CHART_ORDERBOOK_SECTION_TABS.RECENT_TRADES}>
          <RecentTrade />
        </Render>
      </Suspense>
      <SettingsModal
        isOpen={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      />
    </Box>
  );
};

export default ChartOrderBookTab;
